import React, { useRef } from "react"
import { useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import { MedicalInfo } from "../../../types/medicalInfo"
import ResponseError from "#/types/responseError"
import { HttpStatusCodes } from "#/utils/HttpStatusCodes"

const MedicalInfoContext = React.createContext(null)
export const useMedicalInfo = () => React.useContext(MedicalInfoContext)
export const MedicalInfoQueryKey = "carrotPlansMedicalInfo"

// To minimize exposure of PHI, this hook should only be called inside the Medical workflow
export const useGetMedicalInfo = (retry = true): UseQueryResult<MedicalInfo, ResponseError> => {
  return useQuery([MedicalInfoQueryKey], () => carrotClient.getCarrotPlansMedicalInfo(), {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (!retry) return false
      switch (error?.response?.status) {
        case HttpStatusCodes.UNAUTHORIZED:
          return false
        default:
          return failureCount <= 3
      }
    }
  })
}

// @ts-expect-error TS(7031) FIXME: Binding element 'children' implicitly has an 'any'... Remove this comment to see the full error message
export function MedicalInfoContextProvider({ children }): JSX.Element {
  const dedupeRef = useRef(false)
  const queryClient = useQueryClient()

  // @ts-expect-error TS(7006) FIXME: Parameter 'payload' implicitly has an 'any' type.
  async function startMedicalInfo(payload = null): Promise<void> {
    if (!dedupeRef.current) {
      dedupeRef.current = true
      await carrotClient.startMedicalInfoFlow(payload).catch(() => (dedupeRef.current = false))
      await queryClient.refetchQueries({ queryKey: [MedicalInfoQueryKey] }, { throwOnError: true })
      dedupeRef.current = false
    }
  }

  async function updateMedicalInfo(payload: MedicalInfo, retry = true): Promise<void> {
    await carrotClient.updateMedicalInfo(payload).catch(async (error) => {
      if (retry) {
        await startMedicalInfo()
        const medicalInfo: MedicalInfo = queryClient.getQueryData([MedicalInfoQueryKey])
        const catchUp = medicalInfo || {}
        await updateMedicalInfo({ ...catchUp, ...payload }, false)
      } else {
        throw error
      }
    })

    await queryClient
      .refetchQueries({ queryKey: [MedicalInfoQueryKey] }, { throwOnError: true })
      .catch(async (error) => {
        // If we suddenly get a 404 after an update, it means another user (Member or Partner) likely caused the record to be deleted
        // From concurrent editing. Re-attempt recovery stategy.
        if (error?.response?.status === HttpStatusCodes.NOT_FOUND) {
          await startMedicalInfo()
          const medicalInfo: MedicalInfo = queryClient.getQueryData([MedicalInfoQueryKey])
          const catchUp = medicalInfo || {}
          await updateMedicalInfo({ ...catchUp, ...payload }, false)

          await queryClient.refetchQueries({ queryKey: [MedicalInfoQueryKey] }, { throwOnError: true })
        } else {
          throw error
        }
      })
  }

  function hasQuestionBeenSkipped(questionKey: string): boolean {
    const medicalInfo: MedicalInfo = queryClient.getQueryData([MedicalInfoQueryKey])
    return (medicalInfo?.skipFields || []).includes(questionKey)
  }

  return (
    <MedicalInfoContext.Provider
      value={{
        updateMedicalInfo,
        startMedicalInfo,
        hasQuestionBeenSkipped
      }}
    >
      {children}
    </MedicalInfoContext.Provider>
  )
}
